import { useEffect, useRef, useState } from "react";

import TextInput from "../FormInputField/TextInput/TextInput";
import { ReactComponent as DownArrowIcon } from "../../assets/images/down-arrow.svg";
import { ReactComponent as BranchIcon } from "../../assets/images/branch-icon.svg";
import { ReactComponent as CheckIcon } from "../../assets/images/check-icon.svg";

interface BranchSelectProps {
  value: string;
  branches: string[];
  onChange: (branch: string) => void;
  onRefresh?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
}

// Combobox, not a plain select: the field stays typeable on purpose.
//
// The branch listing is capped at one API page, so a repository with hundreds
// of branches would otherwise make the ones past the cap unreachable — and if
// the listing fails altogether (no network, revoked token), typing has to keep
// working rather than blocking app creation. The picker removes the need to
// remember and spell a name; it never becomes the only way in.
//
// One text field does both jobs — holding the branch and searching for it —
// because a second input inside the popup would accept text too, and two boxes
// that both take a branch name is a question the user should not have to
// answer. The list header is what makes the search visible instead of hidden.
const BranchSelect = ({
  value,
  branches,
  onChange,
  onRefresh,
  isLoading,
  isError,
  errorMessage,
}: BranchSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  // Whether what is in the field is something the user is typing (a search) or
  // just the branch currently selected. Without this the field filters against
  // its own value, so opening the list with "master" selected hides every other
  // branch — the picker shows one option and looks broken.
  const [isSearching, setIsSearching] = useState(false);
  // Keyboard cursor. -1 means "nothing highlighted": arrows enter the list from
  // the top, and Enter falls through to the form until something is chosen.
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  const query = value.trim().toLowerCase();
  const matches = branches.filter((branch) =>
    branch.toLowerCase().includes(query)
  );
  // An empty field is not a search: every branch matches it, so a count would
  // read "2 of 2" and tell the user nothing.
  const isFiltering = isSearching && query.length > 0;
  // While typing, the list is the search result — including no result at all,
  // which the empty state explains. Falling back to the full list there would
  // read as "your search was ignored".
  const options = isFiltering ? matches : branches;

  // A changed list invalidates the cursor: keeping the index would highlight
  // whatever moved into that position.
  useEffect(() => {
    setActiveIndex(-1);
  }, [isOpen, value, isSearching]);

  useEffect(() => {
    if (activeIndex < 0) {
      return;
    }

    listRef.current?.children[activeIndex]?.scrollIntoView({
      block: "nearest",
    });
  }, [activeIndex]);

  const select = (branch: string) => {
    onChange(branch);
    setIsSearching(false);
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();

      if (!isOpen) {
        setIsSearching(false);
        setIsOpen(true);
        return;
      }

      if (options.length === 0) {
        return;
      }

      setActiveIndex((previous) => {
        const next = event.key === "ArrowDown" ? previous + 1 : previous - 1;

        if (next < 0) {
          return options.length - 1;
        }

        if (next >= options.length) {
          return 0;
        }

        return next;
      });

      return;
    }

    if (event.key === "Enter" && isOpen) {
      // While the list is open Enter belongs to it, highlighted item or not:
      // letting it through would reach the form and look like the app was
      // being created from inside the branch picker.
      event.preventDefault();

      if (activeIndex >= 0) {
        select(options[activeIndex]);
        return;
      }

      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={containerRef} onKeyDown={handleKeyDown}>
      <TextInput
        value={value}
        label="Branch"
        name="branch"
        onChange={(e) => {
          onChange(e.target.value);
          setIsSearching(true);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholderText="Branch"
        infoText="The repository branch that your web application uses."
        isError={isError}
        // The red border stays, the sentence steps aside: while the list is
        // open the user is already fixing the problem, and the message would
        // wedge itself between the field and the list it belongs to. It comes
        // back the moment the picker closes with the field still empty.
        errorMessage={isOpen ? undefined : errorMessage}
        startAdornment={<BranchIcon className="shrink-0" />}
        endAdornment={
          branches.length > 0 ? (
            <button
              type="button"
              aria-label="Show branches"
              aria-expanded={isOpen}
              className="text-light-blue hover:text-white transition-colors duration-200"
              onClick={() => {
                setIsSearching(false);
                setIsOpen((previous) => !previous);
              }}
            >
              <DownArrowIcon />
            </button>
          ) : undefined
        }
      />
      {isOpen && branches.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 rounded border border-light-blue border-opacity-30 bg-dark-blue shadow-lg overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-light-blue border-opacity-20 text-xs uppercase tracking-wide text-light-blue text-opacity-60">
            <span>
              {isFiltering
                ? `${matches.length} of ${branches.length}`
                : "Type to filter"}
            </span>
            {onRefresh && (
              // A branch pushed after this form opened does not exist in the
              // list yet, and reopening the whole flow to see it is the kind of
              // detour people work around by typing the name blind.
              <button
                type="button"
                className="uppercase tracking-wide text-cta-green hover:underline disabled:no-underline disabled:opacity-60"
                disabled={isLoading}
                onClick={() => onRefresh()}
              >
                {isLoading ? "Refreshing" : "Refresh"}
              </button>
            )}
          </div>
          {options.length > 0 ? (
            <ul
              ref={listRef}
              role="listbox"
              aria-label="Branches"
              className="max-h-56 overflow-y-auto py-1 text-sm"
            >
              {options.map((branch, index) => {
                const isSelected = branch === value;

                return (
                  <li key={branch} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors duration-150 focus:outline-none focus-visible:bg-old-blue/20 ${
                        index === activeIndex
                          ? "bg-old-blue/20"
                          : "hover:bg-old-blue/10"
                      } ${isSelected ? "text-cta-green" : "text-white"}`}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => select(branch)}
                    >
                      <span className="truncate">{branch}</span>
                      {isSelected && (
                        <CheckIcon
                          className="shrink-0"
                          width="14px"
                          height="14px"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="px-3 py-3 text-sm text-light-blue">
              <p>No branch matches what you typed.</p>
              <p className="text-light-blue text-opacity-60 mt-1">
                It will be used exactly as written.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchSelect;
